import React, { useState, useEffect } from "react";
import { X, ChevronLeft, ChevronRight, Maximize, Minimize } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function PresentationView({ study, onClose }) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Build slides array
  const slides = [
    // Title slide
    {
      type: "title",
      content: {
        title: study.title,
        topic: study.topic,
        category: study.category,
      },
    },
    // Scripture slides
    ...study.scriptures.map((scripture) => ({
      type: "scripture",
      content: scripture,
    })),
    // Notes slide (if notes exist)
    ...(study.notes
      ? [
          {
            type: "notes",
            content: { notes: study.notes },
          },
        ]
      : []),
  ];

  const totalSlides = slides.length;

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === "ArrowLeft") {
        handlePrevious();
      } else if (e.key === "ArrowRight") {
        handleNext();
      } else if (e.key === "Escape") {
        if (isFullscreen) {
          exitFullscreen();
        } else {
          onClose();
        }
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [currentSlide, isFullscreen]);

  const handleNext = () => {
    if (currentSlide < totalSlides - 1) {
      setCurrentSlide(currentSlide + 1);
    }
  };

  const handlePrevious = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      // Enter fullscreen
      const elem = document.getElementById("presentation-container");
      if (elem.requestFullscreen) {
        elem.requestFullscreen();
      } else if (elem.webkitRequestFullscreen) {
        elem.webkitRequestFullscreen();
      } else if (elem.msRequestFullscreen) {
        elem.msRequestFullscreen();
      }
      setIsFullscreen(true);
    } else {
      exitFullscreen();
    }
  };

  const exitFullscreen = () => {
    if (document.exitFullscreen) {
      document.exitFullscreen();
    } else if (document.webkitExitFullscreen) {
      document.webkitExitFullscreen();
    } else if (document.msExitFullscreen) {
      document.msExitFullscreen();
    }
    setIsFullscreen(false);
  };

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement) {
        setIsFullscreen(false);
      }
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  const currentSlideData = slides[currentSlide];

  return (
    <div
      id="presentation-container"
      className="fixed inset-0 z-50 bg-gradient-to-br from-amber-50 via-red-50 to-blue-50"
    >
      {/* Control Bar */}
      <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-red-900 to-blue-900 text-white px-6 py-4 flex items-center justify-between shadow-lg z-10">
        <div className="flex items-center space-x-4">
          <Button
            onClick={onClose}
            variant="ghost"
            size="sm"
            className="text-white hover:bg-white/20"
          >
            <X className="w-5 h-5 mr-2" />
            Exit
          </Button>
          <span className="text-sm font-medium">
            Slide {currentSlide + 1} of {totalSlides}
          </span>
        </div>
        <Button
          onClick={toggleFullscreen}
          variant="ghost"
          size="sm"
          className="text-white hover:bg-white/20"
        >
          {isFullscreen ? (
            <>
              <Minimize className="w-5 h-5 mr-2" />
              Exit Fullscreen
            </>
          ) : (
            <>
              <Maximize className="w-5 h-5 mr-2" />
              Fullscreen
            </>
          )}
        </Button>
      </div>

      {/* Slide Content */}
      <div className="absolute inset-0 flex items-center justify-center pt-20 pb-20">
        <div className="w-full max-w-6xl px-8">
          {currentSlideData.type === "title" && (
            <div className="bg-white rounded-3xl shadow-2xl p-16 text-center border-4 border-brown-600">
              <div className="mb-8">
                <img
                  src="/favicon.svg"
                  alt="Soul Aid"
                  className="w-24 h-24 mx-auto mb-6"
                />
              </div>
              <h1 className="text-6xl font-bold text-gray-900 mb-6">
                {currentSlideData.content.title}
              </h1>
              <div className="flex items-center justify-center space-x-4 text-2xl">
                <span className="px-6 py-3 bg-emerald-100 text-emerald-800 rounded-full font-semibold">
                  {currentSlideData.content.topic}
                </span>
                {currentSlideData.content.category && (
                  <span className="px-6 py-3 bg-blue-100 text-blue-800 rounded-full font-semibold">
                    {currentSlideData.content.category}
                  </span>
                )}
              </div>
            </div>
          )}

          {currentSlideData.type === "scripture" && (
            <div className="bg-white rounded-3xl shadow-2xl p-16 border-4 border-blue-600">
              <div className="mb-8">
                <h2 className="text-4xl font-bold text-blue-900 mb-2 text-center">
                  {currentSlideData.content.reference}
                </h2>
              </div>
              <div className="prose prose-xl max-w-none">
                <p className="text-3xl leading-relaxed text-gray-800 text-center italic">
                  "{currentSlideData.content.text}"
                </p>
              </div>
            </div>
          )}

          {currentSlideData.type === "notes" && (
            <div className="bg-white rounded-3xl shadow-2xl p-16 border-4 border-red-600">
              <div className="mb-8">
                <h2 className="text-5xl font-bold text-red-900 mb-6 text-center">
                  Study Notes
                </h2>
              </div>
              <div className="prose prose-xl max-w-none">
                <p className="text-2xl leading-relaxed text-gray-800 whitespace-pre-wrap">
                  {currentSlideData.content.notes}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="absolute bottom-8 left-0 right-0 flex items-center justify-center space-x-6">
        <Button
          onClick={handlePrevious}
          disabled={currentSlide === 0}
          className="bg-white text-gray-900 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed px-8 py-6 text-lg rounded-full shadow-xl"
        >
          <ChevronLeft className="w-6 h-6 mr-2" />
          Previous
        </Button>
        <Button
          onClick={handleNext}
          disabled={currentSlide === totalSlides - 1}
          className="bg-white text-gray-900 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed px-8 py-6 text-lg rounded-full shadow-xl"
        >
          Next
          <ChevronRight className="w-6 h-6 ml-2" />
        </Button>
      </div>
    </div>
  );
}
